/**
 * One exercise, as `Builder planu - tydzien - programator` draws it: a card whose head is one dense
 * row, a set table under it, and a two-column foot carrying the progression statement and the
 * coaching note side by side.
 *
 * ── The head is a row of marks, and the name is the only text in it ──────────────────────────
 * `[✓] [badge] [thumb] [name over its muscle line] … [3 × 10] [warm-up] [⌄] [⋯] [trash]`. The tick appears only
 * while the panel head's superset mode is on; the summary appears only while the card is folded.
 * Everything else is always there, in that order, at the canon's own 9px gaps and 6/10px padding.
 *
 * ── Folding is component state and the SUMMARY is not ────────────────────────────────────────
 * A fold changes nothing that is saved, so it lives in `lib/useDayPanelChrome`. What it prints —
 * "3 × 10" — is `ExerciseView.collapsedSummary`, decided in `model/` against the exercise's FIRST
 * column, so a plank folds to "2 × 45 s" rather than to a repetition count it does not prescribe.
 *
 * ── The foot is where the progression slot lands ─────────────────────────────────────────────
 * The canon puts the rule and the note in one two-column row under the table (1fr / 1.3fr), in both
 * hosts. The rule is `features/plan-progression`'s injected `renderPanel` — a slot rather than an
 * import, because that is a sibling feature and a feature may not import another. The card composes
 * it; it never re-derives what a rule says.
 *
 * The 284px `--bg-inset` side column this file used to draw is gone with it: the programator draws
 * no such column at either width, and keeping it in fullscreen alone would have been two layouts for
 * one card. The last two things that only the fullscreen host drew — the head's type pill and the
 * foot's load-contribution column — went on 13 September 2026 with the host itself: the owner's day
 * modal draws this same card, and `Builder planu - modal - progresja` draws neither.
 *
 * ── The head's `Rozgrzewka` chip ─────────────────────────────────────────────────────────────
 * One press gives THIS movement its own warm-up: it opens the library targeted at the exercise, and
 * the pick lands in this card's own prep box (`insertWarmupFor`). It is not the ⋯'s role move and it
 * is not a second path to it — that entry moves the exercise into the DAY's warm-up run, which
 * belongs to the day and is counted and ordered with it, while this one binds a warm-up to a single
 * lift and makes it die with it.
 *
 * The mock hides the chip on a card inside a bracket (`prepBtn`'s own `display: inGrp ? 'none'`) and
 * so does this: a superset member takes its warm-up by leaving the pair, which the operation does
 * silently, and offering a control whose first act is to break the bracket the trainer just made is
 * a surprise the mock declines to offer. `exercise.addWarmup` is `null` on a warm-up for the other
 * half of the same rule, and `null` on a card that already HOLDS a prep box — which is an owner
 * departure from the mock, argued at the field.
 *
 * The card was said until 8 September 2026 to draw no such control on purpose — because no store
 * operation minted a mini-group. That was true of the code and wrong as a decision, and the
 * operation is the fix; the note is kept here so the reversal reads straight.
 *
 * ── A lift's own warm-ups stand INSIDE its card ──────────────────────────────────────────────
 * The mock's `prepBox`: a `--warm-bg` box behind a 2px `--warm-blue` rail, `4px 9px 0` inside the
 * card at radius 10, headed `Rozgrzewka do ćwiczenia` in the warm-up blue with the count beside it,
 * holding one small card per ramp at radius 9 — each with its own fold, its own set table, its own
 * `+ Seria` and its own remove.
 *
 * Until 8 September 2026 this feature drew the same data the way the WEEK card draws it: a bracket
 * around the ramp and the lift as two rows of the day's list. Same operation, same tree, a different
 * picture of it — and the mock's is the one the trainer sees, so the projection now folds a
 * mini-group into the lift's own block (`dayProgrammingView`) and the frame is gone. `.bracket` is
 * the SUPERSET's alone now.
 *
 * Three things move with the box, and each is the mock's:
 * · the `grpBar` strip appears above it, because what a drag carries is no longer one row but the
 *   lift and its ramps;
 * · a freshly picked ramp plays `cardIn`, keyed off the same "just created" fact that sends focus
 *   to it;
 * · the head's `Rozgrzewka` chip goes, and that one is the owner's departure noted above.
 */

import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';

import { setExerciseNotes } from '~/entities/plan-draft';

import type { ExerciseView, PrepBoxView, Translate } from '../model/dayProgramming';

import { ExerciseIdentity } from './ExerciseIdentity';
import { ExerciseMenu, type ExerciseDayMoveProps } from './ExerciseMenu';
import { DragStrip, type BlockDragSource } from './MoveableBlock';
import { SetTable } from './SetTable';
import styles from './ExerciseBlock.module.css';

/** The superset selection tick, while the panel head has the mode on. */
export interface SupersetPickProps {
  readonly isPicked: boolean;
  readonly onToggle: () => void;
}

interface ExerciseBlockProps {
  readonly exercise: ExerciseView;
  readonly dayRestSeconds: number;
  /** Inside a superset frame — the marker takes the frame's ink, and ⋯ offers unlink. */
  readonly isGrouped: boolean;
  /**
   * The block's drag source, built by `MoveableBlock`: the binding that makes the head row and the
   * `Grupa` strip the two elements a gesture starts from — which is also what the browser lifts as
   * the drag image, so an open card is carried by its head and not whole — and the keyboard lift
   * the ⋯ starts.
   *
   * `null` where nothing in the day may be moved, and on every card that is not the block's own
   * address: a superset's second member, a warm-up inside a prep box, a departing ghost.
   */
  readonly drag: BlockDragSource | null;
  /**
   * This lift's own warm-ups, drawn INSIDE its card — the mock's `prepBox`. `null` on a card with
   * none, which is most of them.
   *
   * The words and the members are the projection's (`PrepBoxView`); the CARDS are the caller's,
   * because each one needs the fold state, the announcer and the read-only flag this component is
   * handed for itself and cannot mint for another exercise. One prop rather than two, so a card
   * either has a prep box or does not and no caller can hand over half of one.
   */
  readonly preps: { readonly view: PrepBoxView; readonly cards: ReactNode } | null;
  /**
   * This card IS one of those warm-ups — the mock's mini card inside the box.
   *
   * It changes the drawing and nothing else: a warm-up drawn in the day's own RUN is a bare row with
   * no ground until it is opened, and one inside a lift's box is a small card on `--bg-card` inside a
   * hairline, exactly as the mock draws the two.
   */
  readonly isPrep: boolean;
  /** The card's foot: `plan-progression`'s rule statement. `null` on a warm-up, which has no rule. */
  readonly progressionPanel: ReactNode;
  /** The cycle matrix, under the set table, when the trainer has expanded it. */
  readonly progressionMatrix: ReactNode;
  /** Folded — the head keeps its marks and prints the summary; everything under it is unmounted. */
  readonly isFolded: boolean;
  readonly onToggleFold: () => void;
  /** `null` unless the superset mode is on — see the header. */
  readonly supersetPick: SupersetPickProps | null;
  /**
   * Open the library targeted at THIS exercise, so the pick becomes its own warm-up.
   *
   * `null` where the host has no library to open — the read-only viewer, and any host that has not
   * wired the target through. A chip that opened a catalogue whose pick landed somewhere else would
   * be worse than no chip.
   */
  readonly onAddWarmup: (() => void) | null;
  /**
   * The ⋯'s "Przenieś do dnia…" group, or `null` where the card offers no such move — a viewer, and
   * the day's standalone warm-up run. Built by the form, which is the level that knows the phase's
   * other days.
   */
  readonly moveToDay: ExerciseDayMoveProps | null;
  /** Where focus lands once this card's removal is confirmed. Forwarded to the ⋯ unchanged. */
  readonly removalFocus: RefObject<HTMLElement | null>;
  readonly t: Translate;
  readonly announce: (message: string) => void;
  /** The day is being READ — the client calendar's preview. Every act on the card goes. */
  readonly readOnly: boolean;
  /**
   * True when this card was JUST created and is owed focus.
   *
   * The builder's library drawer lies over the day, so the successful press closes it and unmounts
   * the row that was pressed; inside a focus trap that drops focus onto `<body>` and the next Tab
   * restarts at the top of the panel. The card takes focus instead — it names the movement, so the
   * landing is announced. Same shape, and same reason, as the week card's `shouldTakeFocus`.
   *
   * `tabIndex={-1}` on the card is what makes it a target: focusable by script, and not a new tab
   * stop in a day that already has one per control.
   */
  readonly shouldTakeFocus: boolean;
}

/** The coaching note, at the foot of the card — one slim dashed field beside the progression row. */
function NoteField({
  exercise,
  readOnly,
}: {
  readonly exercise: ExerciseView;
  readonly readOnly: boolean;
}) {
  return (
    <textarea
      className={styles.noteField}
      /* One line, so the foot is one row: a textarea's intrinsic height comes from `rows`, whose
       * default of 2 overrode the 22px `min-height` the mock draws and made the note the tallest
       * thing in the foot. It still grows as it is typed into and still drags. */
      rows={1}
      aria-label={exercise.note.label}
      maxLength={exercise.note.maxLength}
      readOnly={readOnly}
      // A viewer shows the note the athlete will read, and shows it EMPTY when there is none — the
      // hint is an invitation to write and there is nothing to write with.
      placeholder={readOnly ? undefined : exercise.note.hint}
      // Uncontrolled: the store holds the committed value and the field holds the typing. A
      // controlled textarea writing on every keystroke would push one undo entry per character.
      defaultValue={exercise.note.value}
      onBlur={
        readOnly
          ? undefined
          : (event) => {
              setExerciseNotes(exercise.slotToken, event.target.value);
            }
      }
      onKeyDown={(event) => {
        if (event.key === 'Escape') event.stopPropagation();
      }}
    />
  );
}

/** The head's fold chevron — one control, two names, pointing where a press sends the card. */
function FoldButton({
  exercise,
  isFolded,
  onToggle,
}: {
  readonly exercise: ExerciseView;
  readonly isFolded: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.iconButton}
      aria-expanded={!isFolded}
      aria-label={isFolded ? exercise.fold.expand : exercise.fold.collapse}
      onClick={onToggle}
    >
      <svg
        viewBox="0 0 20 20"
        /* Down while the card is closed, turned while it is open — the mock's own
         * `transform: isOpen ? rotate(180deg) : none` on a chevron whose path points down. */
        className={`${String(styles.iconGlyph)} ${isFolded ? '' : String(styles.foldIconUp)}`}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m6 8 4 4 4-4" />
      </svg>
    </button>
  );
}

/**
 * The superset tick.
 *
 * A real `<input type="checkbox">` with a real accessible name, not a `<span>` carrying a glyph: it
 * has to be reachable by keyboard, announce its own checked state, and be operable by voice control
 * saying the words beside it. An ineligible card keeps the box and takes `aria-disabled` with the
 * reason on it, so a trainer who tabs onto it hears WHY a warm-up cannot join rather than finding
 * a control that silently ignores them.
 */
function SupersetTick({
  exercise,
  pick,
}: {
  readonly exercise: ExerciseView;
  readonly pick: SupersetPickProps;
}) {
  const refusal = exercise.supersetPick.refusalReason;
  const isPicked = refusal === null && pick.isPicked;
  return (
    <span className={styles.pickBox}>
      <input
        type="checkbox"
        className={styles.pick}
        checked={isPicked}
        aria-label={refusal === null ? exercise.supersetPick.label : `${exercise.supersetPick.label}, ${refusal}`}
        aria-disabled={refusal !== null ? 'true' : undefined}
        title={refusal ?? exercise.supersetPick.label}
        onChange={() => {
          if (refusal === null) pick.onToggle();
        }}
      />
      {/* The tick, drawn OVER the box rather than by it: an `<input>` is a replaced element and its
        * `::after` does not render, so a checkbox with `appearance: none` fills and shows nothing.
        * `pointer-events: none` leaves every press on the real control underneath. */}
      {isPicked && (
        <span className={styles.pickMark} aria-hidden="true">
          ✓
        </span>
      )}
    </span>
  );
}

export function ExerciseBlock({
  exercise,
  dayRestSeconds,
  isGrouped,
  drag,
  preps,
  isPrep,
  progressionPanel,
  progressionMatrix,
  isFolded,
  onToggleFold,
  supersetPick,
  onAddWarmup,
  moveToDay,
  removalFocus,
  t,
  announce,
  readOnly,
  shouldTakeFocus,
}: ExerciseBlockProps) {
  const headingId = useId();
  const blockRef = useRef<HTMLDivElement>(null);
  const isIneligible = supersetPick !== null && exercise.supersetPick.refusalReason !== null;

  // Claimed ONCE per request, guarded by the dependency array — the same shape `SessionCard` uses
  // for the day card the dock hands focus back to.
  useEffect(() => {
    if (shouldTakeFocus) blockRef.current?.focus();
  }, [shouldTakeFocus]);

  return (
    <div
      ref={blockRef}
      className={styles.block}
      tabIndex={-1}
      data-grouped={isGrouped ? '' : undefined}
      data-folded={isFolded ? '' : undefined}
      /* One of a lift's own warm-ups, standing in that box — see `isPrep`. */
      data-prep={isPrep ? '' : undefined}
      /* Just added, and only ever set on a prep: the mock plays `cardIn` on the card a targeted
       * library pick just made and on nothing else. It is the same fact `shouldTakeFocus` carries —
       * this card was created a moment ago — read once and used for both. */
      data-fresh={isPrep && shouldTakeFocus ? '' : undefined}
      /* A card in the day's warm-up RUN is drawn as a row, not a card: no ground, no edge and no
       * elevation until it is opened, which is when it gains `--bg-card` and a hairline. The mock
       * draws the run that way and the main work as cards, from one structure. */
      data-warmup={exercise.isWarmup ? '' : undefined}
      /* Dimmed while a selection is running and this card cannot join it — the canon's own way of
       * saying which rows the mode is about, and the checkbox beside it still carries the reason in
       * words. Never colour alone. */
      data-ineligible={isIneligible ? '' : undefined}
      data-picked={supersetPick?.isPicked === true && !isIneligible ? '' : undefined}
    >
      {/* The mock's `grpBar`: the strip a card holding preps is dragged by, standing above the box,
        * because the thing being dragged is no longer the row the trainer is looking at but the
        * lift and its ramps together. It binds the same block the head does. */}
      {preps !== null && (
        <>
          <DragStrip binding={drag?.strip ?? null} className={String(styles.groupBar)}>
            <span className={styles.groupTag}>{preps.view.groupLabel}</span>
            <span className={styles.groupRule} aria-hidden="true" />
          </DragStrip>
          <div className={styles.prepBox} role="group" aria-label={preps.view.label}>
            <span className={styles.prepHead}>
              {/* The mock's own 11px flame. Decorative: the heading beside it is the name. */}
              <svg
                viewBox="0 0 20 20"
                className={styles.prepFlame}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10 3.5c-3 3.4-4.5 6-4.5 8.2a4.5 4.5 0 0 0 9 0c0-2.2-1.5-4.8-4.5-8.2z" />
              </svg>
              <span className={styles.prepTitle}>{preps.view.title}</span>
              <b className={styles.prepCount}>{preps.view.count}</b>
            </span>
            {preps.cards}
          </div>
        </>
      )}

      <DragStrip binding={drag?.strip ?? null} className={String(styles.head)}>
        {supersetPick !== null && <SupersetTick exercise={exercise} pick={supersetPick} />}

        <ExerciseIdentity exercise={exercise} headingId={headingId} isGrouped={isGrouped} reserve={false} />

        <span className={styles.headSpacer} />

        {isFolded && !exercise.isWarmup && exercise.collapsedSummary !== null && (
          <span className={styles.summary}>{exercise.collapsedSummary}</span>
        )}

        {/* The mock's `prepBtn`, in its own place in the row: after the summary, before the fold.
          * The word came off on 15 September 2026 and went back on on 16 September (owner): stripped
          * to a glyph it stood as a fourth identical square in a row of view controls, and this one
          * is the only thing in the row that ADDS to the day. */}
        {exercise.addWarmup !== null && !isGrouped && !readOnly && onAddWarmup !== null && (
          <button
            type="button"
            className={styles.warmupChip}
            // The chip prints one word and six of them stand in a panel, so the NAME carries the
            // movement — and it contains the visible word, which WCAG 2.5.3 requires.
            aria-label={exercise.addWarmup.ariaLabel}
            title={exercise.addWarmup.ariaLabel}
            onClick={onAddWarmup}
          >
            {/* The mock's own pulse trace. Decorative: the word beside it is the name. */}
            <svg
              viewBox="0 0 24 24"
              className={styles.warmupChipIcon}
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M3 12h3.4l1.7-5 3.3 11 2-6 1.3 3H21" />
            </svg>
            {exercise.addWarmup.label}
          </button>
        )}

        {/* No fold in a VIEWER. The client calendar's preview draws one control and it is the way
          * out — a card the trainer can put away is an act on a list they came here to read whole,
          * and the panel head offers no "Rozwiń wszystkie" to undo it with. */}
        {readOnly ? null : (
          <>
            <FoldButton exercise={exercise} isFolded={isFolded} onToggle={onToggleFold} />
            <ExerciseMenu
              exercise={exercise}
              isGrouped={isGrouped}
              move={moveToDay}
              lift={drag?.lift ?? null}
              removalFocus={removalFocus}
              t={t}
              announce={announce}
            />
          </>
        )}
      </DragStrip>

      {exercise.ladderWarning !== null && !isFolded && (
        <p className={styles.warning} role="status">
          {exercise.ladderWarning}
        </p>
      )}

      {/* Unmounted rather than hidden while folded: a `display: none` table keeps sixty inputs in
        * the tab order of a day the trainer has just told the panel to put away. */}
      {!isFolded && (
        <>
          <div className={styles.main}>
            <SetTable
              exercise={exercise}
              dayRestSeconds={dayRestSeconds}
              labelledBy={headingId}
              announce={announce}
              readOnly={readOnly}
              changes={null}
            />

            {/* The matrix sits BELOW the set table, so what the rule does over the phase reads as a
              * consequence of the prescription above it. */}
            {progressionMatrix}
          </div>

          {/* One column, not two, on a card with no rule to state — a warm-up. An empty 1fr track
            * beside the note would read as a group that failed to load. */}
          <div
            className={styles.foot}
            data-columns={progressionPanel === null ? 'one' : undefined}
          >
            {progressionPanel}

            {/* No heading over it, as the mock draws it: the field carries its own accessible name
              * ("Wskazówka do ćwiczenia: <ruch>") and its own placeholder, so a printed label above
              * a 22px box was a third statement of the same thing in a row that has to stay one
              * line tall beside the progression chip. */}
            <NoteField exercise={exercise} readOnly={readOnly} />
          </div>
        </>
      )}
    </div>
  );
}
